import type { TestGroupingGroup, TestGroupingItem } from '@vedalib/editor/lib/elements'
import { AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { useContext } from 'use-context-selector'

import TestGroupingContext from '../../TestGroupingContext'
import * as s from './MobileGroup.module.scss'
import MobileGroupStack from './MobileGroupStack'
import MobileScaledAnswer from './MobileScaledAnswer'

type CarouselItem =
  | { type: 'group'; data: TestGroupingGroup }
  | { type: 'answer'; data: TestGroupingItem }

interface IMobileGroupProps {
  group: TestGroupingGroup
}

const MobileGroup = ({ group }: IMobileGroupProps) => {
  const { styles, destination } = useContext(TestGroupingContext)
  const [stackIndex, setStackIndex] = useState(0)
  const [animatingItem, setAnimatingItem] = useState<TestGroupingItem | null>(null)
  const previousItemsRef = useRef<TestGroupingItem[]>(group.items || [])

  const items = group.items || []
  const totalStackItems = items.length + 1

  const cardSize = {
    width: styles.common.width,
    height: styles.common.height,
  }

  useEffect(() => {
    const currentItems = group.items || []
    const previousItems = previousItemsRef.current

    const newItem = currentItems.find(
      (item) => !previousItems.some((prevItem) => prevItem.value === item.value),
    )

    if (newItem) {
      setAnimatingItem(newItem)
      setTimeout(() => {
        setAnimatingItem(null)
      }, 400)
    }

    previousItemsRef.current = currentItems
  }, [group.items, group.value, destination])

  const handleStackPrevious = (e: React.MouseEvent) => {
    e.stopPropagation()
    setStackIndex((prev) => (prev === 0 ? totalStackItems - 1 : prev - 1))
  }

  const handleStackNext = (e: React.MouseEvent) => {
    e.stopPropagation()
    setStackIndex((prev) => (prev + 1) % totalStackItems)
  }

  const stackItems = animatingItem
    ? items.filter((item) => item.value !== animatingItem.value)
    : items

  const carouselItems: CarouselItem[] = [
    { type: 'group', data: group },
    ...stackItems.map((item) => ({ type: 'answer' as const, data: item })),
  ]

  return (
    <div className={s.groupWrapper} style={cardSize}>
      <MobileGroupStack
        cardSize={cardSize}
        carouselItems={carouselItems}
        handleStackNext={handleStackNext}
        handleStackPrevious={handleStackPrevious}
        stackIndex={stackIndex}
      />
      <AnimatePresence mode='wait'>
        {animatingItem && (
          <div className={s.scaledAnswerContainer} style={cardSize}>
            <MobileScaledAnswer item={animatingItem} key={animatingItem.value} />
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MobileGroup
